import { useEffect, useRef, useState } from "react";

import useViewport from "./use-viewport";

interface TableHeightResult {
	height: number;
	boxRef: React.MutableRefObject<HTMLDivElement | null>;
}

const useTableHeight = (): TableHeightResult => {
	const { height } = useViewport();
	const boxRef = useRef<HTMLDivElement | null>(null);
	const [y, setY] = useState(0);
	const [isFirstRender, setIsFirstRender] = useState(true);

	// This function calculate X and Y
	const getPosition = () => {
		try {
			const element = boxRef.current;
			if (element) {
				const rect = element.getBoundingClientRect();
				const yPos = rect.top;
				// Only update if the element is visible and has a position
				if (rect.width > 0 || rect.height > 0) {
					setY(yPos);
					setIsFirstRender(false);
				}
			}
		} catch (e) {
			// Do nothing
		}
	};
	useEffect(() => {
		const timeoutId = setTimeout(getPosition, 100);
		return () => clearTimeout(timeoutId);
	}, []);
	// Re-calculate X and Y on resize
	useEffect(() => {
		const handleResize = () => {
			getPosition();
		};
		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);
	// Re-calculate when content above may have changed layout
	useEffect(() => {
		// Use ResizeObserver to detect when the element's position changes due to sibling/parent layout shifts
		if (!boxRef.current) return;
		const parent = boxRef.current.parentElement;
		if (!parent) return;
		const observer = new ResizeObserver(() => {
			getPosition();
		});
		observer.observe(parent);
		return () => observer.disconnect();
	}, []);

	return {
		height: (height && !isFirstRender) ? Math.max(height - y - 24, 200) : 200,
		boxRef,
	};
};

export default useTableHeight;
